import { getCountryCodeFromLocale } from '@trustoo/funcs/locale'
import { CountryCode, StandardizedLocale } from '@trustoo/types/locale'
import { getS3Url } from '@trustoo/utils/s3'

export interface CountrySettings {
    /**
     * Lazily imports the localized country-name map (used by the phone input country picker).
     */
    countryNamesImport: () => Promise<{ default: Record<string, string> }>
    /**
     * Example phone number to display as the input placeholder. When omitted, the phone input
     * falls back to the libphonenumber example number for the country.
     */
    exampleNumber?: string
}

/**
 * Per-country settings shared across apps (used by the phone input component).
 */
export const COUNTRY_SETTINGS: Record<CountryCode, CountrySettings> = {
    NL: {
        countryNamesImport: () => import('react-phone-number-input/locale/nl.json'),
    },
    BE: {
        countryNamesImport: () => import('react-phone-number-input/locale/nl.json'),
        exampleNumber: '0123 45 67 89',
    },
    DE: {
        countryNamesImport: () => import('react-phone-number-input/locale/de.json'),
    },
    ES: {
        countryNamesImport: () => import('react-phone-number-input/locale/es.json'),
    },
}

export const getClientLocale = (): StandardizedLocale => {
    const { hostname, pathname } = window.location
    const isEnglish = pathname.split('/').filter(Boolean)[0] === 'en'

    if (hostname.includes('trustlocal.be')) {
        return isEnglish ? 'en-BE' : 'nl-BE'
    }

    if (hostname.includes('trustlocal.de')) {
        return isEnglish ? 'en-DE' : 'de-DE'
    }

    if (hostname.includes('trustlocal.es')) {
        return isEnglish ? 'en-ES' : 'es-ES'
    }

    return isEnglish ? 'en-NL' : 'nl-NL'
}

export const getClientCountryCode = (): CountryCode => getCountryCodeFromLocale(getClientLocale())

/** The variants of a market's logo, as URLs in the public S3 bucket. */
export interface LogoUrls {
    default: string
    white: string
    whiteAlt: string
    png: string
    small: string
}

/**
 * The logo of the brand a country runs under: Trustoo in the Netherlands, Trustlocal everywhere
 * else.
 */
export const getLogoUrls = (countryCode: CountryCode = 'NL'): LogoUrls => {
    if (countryCode === 'NL') {
        return {
            default: getS3Url('trustoologo_2+.svg'),
            white: getS3Url('trustoologo_white.svg'),
            whiteAlt: getS3Url('trustoologo_white_orange.svg'),
            png: getS3Url('trustoologo_2.png'),
            small: getS3Url('icon/trustoologo_small_light.svg'),
        }
    }

    return {
        default: getS3Url('trustlocal_logo_be_2+.svg'),
        white: getS3Url('trustlocal_logo_be_white.svg'),
        whiteAlt: getS3Url('trustlocal_logo_be_white.svg'),
        png: getS3Url('trustlocal_logo_be_2.png'),
        small: getS3Url('icon/trustlocal_small_light.svg'),
    }
}
